import { stripTime } from "@/utils/Dates";
import ContractEndTypeEnum from "@/constants/enums/ContractEndTypeEnum";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import TransportResponsibleEnum from "@/constants/enums/TransportResponsibleEnum";
import {
  ContractTradeType,
  ContractType,
  CreateAddress,
  CreateContract,
  CurrencyCode,
  TransportResponsibleType,
  WeightResponsibleType,
  MeasureUnitType,
  CompanySpecificService,
  ContractDeviation,
  ContractDeviationType,
  InvoiceDueDate,
  ArchiveDocument,
} from "@/types";
import WeightResponsibleEnum from "@/constants/enums/WeightResponsibleEnu";
import MeasureUnitEnum from "@/constants/enums/MeasureUnit";
import dayjs from "dayjs";

export interface CreateContractWizard {
  name: string;
  description: string;
  tradingPartner: number;
  contractDate: Date;
  contractStartDate: Date;
  contractEndDate: Date | null;
  companySpecificServices: CompanySpecificService[];
  contractDeviations: ContractDeviation[];
  companySpecificProductId: number;
  companySpecificProductMeasureUnit: MeasureUnitType;
  useTransportServices: boolean;
  amount: string;
  agreedBuyerPricePrUnit: string;
  agreedSellerPricePrUnit: string;
  selectedServices: number[];
  invoiceIntervalId: number;
  invoiceDueDateId: InvoiceDueDate;
  transportResponsible: TransportResponsibleType;
  currencyType: CurrencyCode;
  comment: string;
  amountUnitId: number;
  priceUnitId: number;
  transportFromAddress?: CreateAddress | null;
  transportToAddress?: CreateAddress | null;
  attachments?: ArchiveDocument[] | null;
  attachmentUUIDs: string[];
  automaticSalesApproval: string;
  contractEndType: ContractType;
  contractType: ContractTradeType;
  selectFromExisting: boolean;
  weightResponsible: WeightResponsibleType;
  invoiceInformation: string;
  contractManagerId: number;
  contactEmail: string;
  contactPhone: string;
  counterpartyContactEmail: string;
  counterpartyContactPhone: string;
  companySpecificServicesOptions: any[];
  contractDeviationOptions: ContractDeviationType[];
  kgPerMeter: number | null;
  kgPerM3: number | null;
  kgPerStk: number | null;
  tradeAtOwnRisk: boolean;
  isPaymentReceiverInverted: boolean;
  template: string;
  templateName: string | null;
  poNumber: string | null;
  skipInvoicing: boolean;
  indexRegulated: boolean;
}

export const CreateNewContractPlaceholderObj: CreateContractWizard = {
  name: "",
  description: "",
  tradingPartner: 0,
  contractDate: stripTime(dayjs().toDate()),
  contractStartDate: stripTime(dayjs().toDate()),
  companySpecificProductId: 0,
  useTransportServices: false,
  agreedBuyerPricePrUnit: "",
  agreedSellerPricePrUnit: "",
  selectedServices: [],
  invoiceIntervalId: 1,
  invoiceDueDateId: { id: 1, days: 7 },
  transportResponsible: TransportResponsibleEnum.SELLER,
  weightResponsible: WeightResponsibleEnum.SELLER,
  currencyType: "NOK",
  priceUnitId: 1,
  contractEndDate: null,
  amount: "",
  comment: "",
  amountUnitId: 2,
  transportFromAddress: undefined,
  transportToAddress: undefined,
  attachments: null,
  contractEndType: ContractEndTypeEnum.AMOUNT,
  contractType: ContractTradeTypeEnum.BUY,
  selectFromExisting: true,
  automaticSalesApproval: "YES",
  invoiceInformation: "",
  contractManagerId: 0,
  contactEmail: "",
  contactPhone: "",
  counterpartyContactEmail: "",
  counterpartyContactPhone: "",
  companySpecificServices: [],
  companySpecificServicesOptions: [],
  companySpecificProductMeasureUnit: MeasureUnitEnum.KG,
  attachmentUUIDs: [],
  kgPerMeter: 0,
  kgPerM3: 0,
  kgPerStk: 0,
  contractDeviations: [],
  contractDeviationOptions: [],
  tradeAtOwnRisk: false,
  isPaymentReceiverInverted: false,
  template: "NO",
  templateName: null,
  poNumber: null,
  skipInvoicing: false,
  indexRegulated: false,
};

export function ConvertFormToContract(form: CreateContractWizard, companyId: number) {
  console.log("convert form to contract", form);
  const newContract: CreateContract = {
    name: form.name,
    description: form.description,
    contractBuyers: [],
    contractSellers: [],
    contractDate: form.contractDate,
    contractStartDate: form.contractStartDate,
    contractEndDate: form.contractEndType === ContractEndTypeEnum.DATEPERIOD ? form.contractEndDate : null,
    companySpecificProductId: form.companySpecificProductId,
    useTransportServices: form.useTransportServices,
    amount: form.contractEndType === ContractEndTypeEnum.AMOUNT && form.amount ? parseInt(form.amount) : 0,
    agreedBuyerPricePrUnit: 0,
    agreedSellerPricePrUnit: 0,
    selectedServices: form.selectedServices,
    invoiceIntervalId: form.invoiceIntervalId,
    invoiceDueDateId: form.invoiceDueDateId.id,
    transportResponsible: form.transportResponsible,
    currencyType: form.currencyType,
    comment: form.comment,
    amountUnitId: form.amountUnitId,
    priceUnitId: form.priceUnitId,
    transportFromAddress: form.transportFromAddress,
    transportToAddress: form.transportToAddress,
    attachments: form.attachments,
    automaticSalesApproval: form.automaticSalesApproval === "YES",
    weightResponsible: form.weightResponsible,
    invoiceInformation: "",
    contractManagerId: form.contractManagerId,
    contact: { email: form.contactEmail, phone: form.contactPhone },
    counterpartyContact: {
      email: form.counterpartyContactEmail,
      phone: form.counterpartyContactPhone,
    },
    companySpecificServices: form.companySpecificServices.map((service: CompanySpecificService) => ({
      companySpecificServiceId: service.id,
      price: service.price,
      currencyType: service.currency,
    })),
    tradeAtOwnRisk: form.tradeAtOwnRisk,
    previousContractId: null,
    contractDeviations: form.contractDeviations,
    template: form.template === "YES",
    templateName: form.templateName,
    attachmentUUIDs: form.attachmentUUIDs,
    poNumber: form.poNumber,
    skipInvoicing: form.skipInvoicing,
    indexRegulated: form.indexRegulated,
  };

  if (form.contractType === ContractTradeTypeEnum.BUY) {
    newContract.contractBuyers.push(companyId);
    newContract.contractSellers.push(form.tradingPartner);
  } else if (form.contractType === ContractTradeTypeEnum.SELL) {
    newContract.contractSellers.push(companyId);
    newContract.contractBuyers.push(form.tradingPartner);
  }

  if (form.isPaymentReceiverInverted) {
    newContract.agreedBuyerPricePrUnit = -Math.abs(parseFloat(form.agreedBuyerPricePrUnit));
    newContract.agreedSellerPricePrUnit = -Math.abs(parseFloat(form.agreedSellerPricePrUnit));
  } else {
    newContract.agreedBuyerPricePrUnit = Math.abs(parseFloat(form.agreedBuyerPricePrUnit));
    newContract.agreedSellerPricePrUnit = Math.abs(parseFloat(form.agreedSellerPricePrUnit));
  }

  return newContract;
}
